import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

interface DeleteConfirmDialogProps {
    open: boolean
    onClose: () => void
    onConfirm: () => Promise<void>
    isLoading: boolean
    title: string
    itemName: string
    itemType: 'section' | 'row'
}

const DeleteConfirmDialog = ({
    open,
    onClose,
    onConfirm,
    isLoading,
    title,
    itemName,
    itemType,
}: DeleteConfirmDialogProps) => {
    const handleClose = () => {
        if (isLoading) return
        onClose()
    }

    const warningMessage =
        itemType === 'section'
            ? 'This will permanently delete this section and all its rows, including all cells and their content.'
            : 'This will permanently delete this row and all its cells.'

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="18x600">{title}</Typography>
                    <IconButton
                        aria-label="close"
                        disabled={isLoading}
                        onClick={handleClose}
                        sx={{ color: 'figma.grayscale.tier1' }}
                    >
                        <HighlightOffIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack gap={2}>
                    <Typography variant="14x400" color="figma.grayscale.textHier2">
                        Are you sure you want to delete <strong>{itemName}</strong>?
                    </Typography>
                    <Typography variant="14x400" color="error.main">
                        {warningMessage}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button variant="outlined" onClick={handleClose} disabled={isLoading} sx={{ borderRadius: '14px' }}>
                    Cancel
                </Button>
                <LoadingButton
                    loading={isLoading}
                    variant="contained"
                    color="error"
                    onClick={onConfirm}
                    sx={{ borderRadius: '14px', px: 3 }}
                >
                    Delete
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export default DeleteConfirmDialog
